import { adminRoot } from 'src/config'

/**
 * @typedef {Object} MenuItem
 * @property {number} indent - The indentation level of the item when shown in certain components.
 * @property {string} label - The label of the menu item.
 * @property {string} description - The description of the menu item, including the adjustment type and its lowercase pluralized form.
 * @property {string} [icon] - The icon class name of the menu item.
 * @property {string} [route] - The route path, including any URL params, to navigate to when the item is selected.
 * @property {string} [method] - An alternative to `route`, the name of a method to call when the item is selected.
 * @property {number} [notificationCount] - Value for notification count badge.
 * @property {string | string[]} [requiresPermission] - The permission(s), *all* of which are required to view the menu item.
 * @property {string | string[]} [requiresAnyPermission] - The permission(s), *any* of which are required to view the menu item.
 * @property {string} [hideUnless] - The name of the Vue component method to call to determine whether to hide the menu item.
 * @property {boolean} [customerio] - If true, show item to non-super users only if user is admin and the currentCircuit.customerioWorkspaceIsConfigured === true.
 * @property {boolean} [library] - Alternative of `libraryLevel`. If true, show item even if the current site is a library site.
 * @property {string} [libraryLevel] - Alternative of `library`. The library level of the item, if any.
 * @property {boolean} [circuit] - If true, show item to non-super users only if user is authorized to manage their own circuit (which should be the current circuit).
 * @property {boolean} [onlyTemplate] - If true, show item only if currentCircuit.isTemplate === true.
 * @property {MenuItem[]} [entries] - The sub-items of the menu item.
 */

export const LIBRARY_LEVEL = Object.freeze({
  REGION: 'region',
  CIRCUIT: 'circuit',
  ANY: 'any',
  NONE: 'none'
})

/**
 * This mixin allows for filtering a list of menu items based on menu item configuration and
 * current app data. This mixin requires that the following properties be present on the component.
 * These can be either static properties set in `beforeCreate()` or `data()` properties:
 * - `menuItems` (required) - The original, unfiltered array of menu items to filter.
 * - `basePath` (optional)  - The URL path to prepend to each menu item's route.
 */
export default {
  data () {
    return {
      filteredList: [],
      searchTerms: ''
    }
  },
  context: ['currentUser'],
  configuration: ['currentSite', 'currentCircuit'],
  watch: {
    searchTerms: {
      handler () {
        if (!_.isArray(this.menuItems)) {
          throw new Error('this.menuItems must be an array')
        }

        if (!this._debouncedUpdateFilteredList) {
          this._debouncedUpdateFilteredList = _.debounce(this.updateFilteredList, 100)
        }
        this._debouncedUpdateFilteredList()
      },
      immediate: true
    },
    '$route.params.category': {
      handler () {
        this._debouncedUpdateFilteredList()
      }
    },
    libraryLevel () {
      this._debouncedUpdateFilteredList()
    },
    '$context.previewedPermittedFrontendActions' () {
      this._debouncedUpdateFilteredList()
    }
  },
  computed: {
    isLibrary () {
      return this.currentUser.circuitId === 0 || this.currentSite.isLibrary
    },
    libraryLevel () {
      if (this.currentCircuit.isLibrary && this.currentSite.isLibrary) return LIBRARY_LEVEL.REGION
      else if (this.currentSite.isLibrary) return LIBRARY_LEVEL.CIRCUIT
      else return LIBRARY_LEVEL.NONE
    },
    isTemplate () {
      return this.currentCircuit.isTemplate
    }
  },
  methods: {
    updateFilteredList () {
      this.filteredList = this.buildList(this.menuItems)
    },
    /**
     * @param {MenuItem} item - The parent menu item, if any.
     */
    selectItem (item) {
      if (_.isFunction(this.trackSelectItem)) {
        this.trackSelectItem(item)
      }
      if (item.method) {
        this[item.method]()
      }
    },
    /**
     * @param {MenuItem[]} list - The unfiltered list of menu items.
     * @param {MenuItem} [parentItem] - The parent menu item, if any.
     * @param {boolean} [ignoreCurrentRoute] - Whether to ignore the current route when filtering.
     * @returns {MenuItem[]} The filtered array of menu items.
     */
    buildList (list, parentItem = null, ignoreCurrentRoute = false) {
      const isTopCategoryLevel = !parentItem
      let builtList = _.map(list, _.clone)

      // Build URLs for each item before passing them to any sub-items.
      _.each(builtList, listItem => {
        if (!!listItem.route && _.startsWith(listItem.route, '/')) {
          // Only build route once
          return true
        }

        const routePaths = []
        if (isTopCategoryLevel) {
          routePaths.push(adminRoot)
          if (this.basePath) routePaths.push(this.basePath)
        } else if (parentItem) {
          routePaths.push(parentItem.route || _.kebabCase(parentItem.label))
        }
        routePaths.push(listItem.route || _.kebabCase(listItem.label))
        listItem.route = _.join(_.map(routePaths, path => _.trimEnd(path, '/')), '/')
      })

      // Recursively build sub-items.
      _.each(builtList, listItem => {
        if (!_.isEmpty(listItem.entries)) {
          listItem.entries = this.buildList(listItem.entries, listItem)
        }
      })

      builtList = _.filter(builtList, this.filterPermittedItems)
      builtList = _.filter(builtList, listItem => !listItem.hideUnless || this[listItem.hideUnless]())
      builtList = _.filter(builtList, this.filterLibraryAllowedItems)
      builtList = _.filter(builtList, this.filterLibraryLevelSpecificItems)
      builtList = _.filter(builtList, this.filterAuthorizedCircuitOnlyItems)
      builtList = _.filter(builtList, this.filterCustomerioDependentItems)
      builtList = _.filter(builtList, this.filterTemplateOnlyItems)
      if (isTopCategoryLevel) {
        builtList = _.filter(builtList, listItem => this.listItemMatchesSearchTerms(listItem, this.searchTerms))
        if (!ignoreCurrentRoute) {
          builtList = _.filter(builtList, listItem => this.listItemMatchesCategoryPath(listItem, this.$route.params.category))
        }
        if (_.isEmpty(this.$route.params.category) && !ignoreCurrentRoute) {
          builtList = _.filter(builtList, listItem => !_.isEmpty(listItem.entries))
        }
      }

      return builtList
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @param {string} searchTerms - Non-empty search terms to match against.
     * @returns {boolean} - Whether the menu item, or one of its sub item, matches the search terms.
     */
    listItemMatchesSearchTerms (listItem, searchTerms) {
      if (_.isEmpty(searchTerms)) return true

      const selfMatches = _.includes(_.toLower(listItem.label), _.toLower(searchTerms))
      if (selfMatches) return true

      const matchingEntries = _.filter(listItem.entries, (entry) => this.listItemMatchesSearchTerms(entry, searchTerms))
      if (!_.isEmpty(matchingEntries)) {
        listItem.entries = matchingEntries
        return true
      }
      return false
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @param {string} category - Non-empty category (in kebab-case) to filter to.
     * @returns {boolean} - Whether the menu item, or one of its sub item, matches the ?category= URL param.
     */
    listItemMatchesCategoryPath (listItem, categoryPath) {
      if (_.isEmpty(categoryPath)) return true

      return _.kebabCase(_.toLower(listItem.label)) === categoryPath
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @returns {boolean} - True if the menu item is permitted, false otherwise.
     */
    filterPermittedItems (listItem) {
      if (!_.isEmpty(listItem.requiresPermission)) {
        return this.$isPermitted(listItem.requiresPermission)
      } else if (!_.isEmpty(listItem.requiresAnyPermission)) {
        return this.$isAnyPermitted(listItem.requiresAnyPermission)
      }
      return true
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @returns {boolean} - Whether to show the menu item even if currently viewing library.
     */
    filterLibraryAllowedItems (listItem) {
      // If the item has a `libraryLevel` option, let the library level filter handle it
      if (_.has(listItem, 'libraryLevel')) return true
      if (this.isLibrary && !listItem.library) return false
      return true
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @returns {boolean} - Whether to show or hide the menu item based on whether it's library level matches the current library level.
     */
    filterLibraryLevelSpecificItems (listItem) {
      // If the item has a `library` option, let the library allow filter handle it
      if (_.has(listItem, 'library')) return true
      if (!listItem.libraryLevel) return true

      // If we're not viewing a library, then show items
      if (this.libraryLevel === LIBRARY_LEVEL.NONE) return true

      // If we want to show at any level of library, then show as long as we're viewing a library
      if (listItem.libraryLevel === LIBRARY_LEVEL.ANY && this.libraryLevel !== LIBRARY_LEVEL.NONE) return true

      // Otherwise, hide the item if the library level doesn't match
      if (listItem.libraryLevel !== this.libraryLevel) return false

      return true
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @returns {boolean} - Whether to show or hide the menu item based on whether it requires circuit authorization.
     */
    filterAuthorizedCircuitOnlyItems (listItem) {
      if (!listItem.circuit) return true

      return this.currentUser.superUser || this.currentUser.circuitAuthorized
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @returns {boolean} - Whether to show or hide the menu item based on whether it requires customerio configuration.
     */
    filterCustomerioDependentItems (listItem) {
      if (!listItem.customerio) return true

      return this.currentUser.superUser || (this.currentUser.admin && this.currentCircuit.customerioWorkspaceIsConfigured)
    },
    /**
     * @param {MenuItem} listItem - The menu item to check.
     * @returns {boolean} - Whether to show or hide the menu item based on whether the item is for templates only and whether
     * current circuit is a template.
     */
    filterTemplateOnlyItems (listItem) {
      if (!listItem.onlyTemplate) return true

      return this.isTemplate
    },
    /**
     *
     * @returns {boolean} - Whether the current site has polaris enabled.
     */
    isPolarisEnabled () {
      return this.currentSite.polarisEnabled
    },
    /**
     *
     * @returns {boolean} - Whether the current site has inventory enabled.
     */
    inventoryOperationsAreEnabled () {
      const siteHasAnActiveInventoryStrategy = !!this.currentSite.activeInventoryStrategy
      const siteIsNotALibrary = !this.currentSite.isLibrary
      return siteHasAnActiveInventoryStrategy && siteIsNotALibrary
    },
    inventorySettingsAreEnabled () {
      const siteHasAnActiveInventoryStrategy = !!this.currentSite.activeInventoryStrategy
      const siteIsALibrary = this.currentSite.isLibrary
      return siteHasAnActiveInventoryStrategy || siteIsALibrary
    },
    priceTierSettingsAreEnabled () {
      return this.currentCircuit.priceTiersEnabled
    }
  }
}
